import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { useDragLayer, XYCoord } from "react-dnd";
import { useAppSelector } from "redux/hooks";
import { RootState } from "utils/store";

type Props = {
  enabled: boolean;
  isMobile?: boolean;
};

function getSignerTypeLabel(signer: any) {
  return signer?.cc ? "CC Receives a Copy" : "Need to Sign";
}

function getSecurityTypeLabel(signer: any): string | null {
  const rs = signer?.requestSecurity;
  if (!rs?.needSecuredAccess) return null;
  return rs?.needOTPValidation ? "OTP verification" : "Security code";
}

function getLayerItemStyles(currentOffset: XYCoord | null) {
  if (!currentOffset) {
    return { display: "none" as const };
  }

  const { x, y } = currentOffset;
  return {
    transform: `translate(${x}px, ${y}px)`,
  };
}

const SignerPreviewCard = ({
  color,
  name,
  email,
  signerType,
  securityType,
  isMobile,
  previewWidth,
}: {
  color?: string;
  name?: string;
  email?: string;
  signerType: string;
  securityType?: string | null;
  isMobile?: boolean;
  previewWidth?: string;
}) => {
  const hasName = Boolean(name && String(name).trim());
  const hasEmail = Boolean(email && String(email).trim());

  return (
    <Paper
      elevation={3}
      sx={{
        width: "100%",
        borderRadius: "8px",
        border: "1px solid #ddd",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {isMobile ? (
        <Box
          sx={{
            height: "6px",
            width: "100%",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            backgroundColor: color,
          }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            width: "8px",
            top: 0,
            bottom: 0,
            left: 0,
            backgroundColor: color,
            borderRadius: "8px 0px 0px 8px",
          }}
        />
      )}

      <Box sx={{ p: 1.25, ...(isMobile ? {} : { pl: 2.5, position: "relative" }) }}>
        <Stack spacing={0.5}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              lineHeight: 1.2,
              color: hasName ? "text.primary" : "text.disabled",
            }}
            noWrap
          >
            {hasName ? name : "Enter Name"}
          </Typography>
          <Typography
            variant="body2"
            color={hasEmail ? "text.secondary" : "text.disabled"}
            noWrap
          >
            {hasEmail ? email : "Enter Email"}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="caption" sx={{ color: "text.primary" }}>
              {signerType}
            </Typography>
            {securityType ? (
              <>
                <Typography variant="caption" sx={{ color: "text.disabled" }}>
                  •
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {securityType}
                </Typography>
              </>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

export default function SignerDragLayer({ enabled, isMobile }: Props) {
  const { signRequest } = useAppSelector((state: RootState) => state.app);
  const cardPreviewWidth = isMobile
    ? "clamp(220px, calc(100vw - 24px), 560px)"
    : "clamp(300px, 40vw, 560px)";
  const groupPreviewWidth = isMobile
    ? "clamp(240px, calc(100vw - 16px), 600px)"
    : "clamp(320px, 42vw, 600px)";

  const { isDragging, itemType, item, currentOffset } = useDragLayer(
    (monitor) => ({
      item: monitor.getItem() as any,
      itemType: monitor.getItemType(),
      isDragging: monitor.isDragging(),
      currentOffset: monitor.getClientOffset(),
    }),
  );

  if (!enabled || !isDragging || itemType !== "signer") {
    return null;
  }

  const dragType = item?.dragType;

  let previewSigners: any[] = [];
  if (dragType === "SINGLE") {
    const signer = signRequest?.signers?.find((s: any) => s.id === item?.id) ?? null;
    previewSigners = signer ? [signer] : [];
  } else if (dragType === "GROUP") {
    const groupIds: number[] = item?.groupSignerIds ?? [];
    previewSigners = groupIds
      .map((id) => signRequest?.signers?.find((s: any) => s.id === id))
      .filter(Boolean);
  } else if (dragType === "GROUP_MEMBER") {
    const signer =
      signRequest?.signers?.find((s: any) => s.id === item?.signerId) ?? null;
    previewSigners = signer ? [signer] : [];
  }

  if (!previewSigners.length) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        pointerEvents: "none",
        zIndex: 2000,
        left: 0,
        top: 0,
        width: "100%",
        height: "100%",
      }}
    >
      <Box
        sx={{
          ...getLayerItemStyles(currentOffset),
          willChange: "transform",
          transformOrigin: "0 0",
        }}
      >
        {dragType === "GROUP" ? (
          <Paper
            elevation={4}
            sx={{
              p: 1,
              borderRadius: "10px",
              border: "1px solid #E5E7EB",
              backgroundColor: "#F8FAFC",
              width: groupPreviewWidth,
              maxWidth: "calc(100vw - 12px)",
            }}
          >
            <Stack spacing={1}>
              {previewSigners.map((signer: any) => (
                <SignerPreviewCard
                  key={signer.id}
                  isMobile={isMobile}
                  previewWidth="100%"
                  color={signer.color}
                  name={signer.name}
                  email={signer.email}
                  signerType={getSignerTypeLabel(signer)}
                  securityType={getSecurityTypeLabel(signer)}
                />
              ))}
            </Stack>
          </Paper>
        ) : (
          <Box sx={{ width: cardPreviewWidth, maxWidth: "calc(100vw - 16px)" }}>
            <Stack spacing={1}>
              {previewSigners.map((signer: any) => (
                <SignerPreviewCard
                  key={signer.id}
                  isMobile={isMobile}
                  previewWidth="100%"
                  color={signer.color}
                  name={signer.name}
                  email={signer.email}
                  signerType={getSignerTypeLabel(signer)}
                  securityType={getSecurityTypeLabel(signer)}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Box>
  );
}

